import React from 'react'

import { useIntl } from 'react-intl'
import { LoanRole } from '@matech/thebigpos-sdk'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import CircularProgress from '@mui/material/CircularProgress'

import { IconButton } from '../Button'
import { LoanUserRow } from './LoanUsersTable'
import useRoleList from '../../hooks/useRoleList'
import { LabelValue } from '../../types/LabelValue'
import FormControl from '../formControls/FormControl'
import { addSpacesToPascalCase } from '../../services/helpers'
import getAllowedRolesForUser from '../../utils/rolePermissions'
import Select, { SelectChangeEvent } from '../formControls/Select'

type EditingRowState = {
	role: LoanRole
	loading: boolean
}

type EditingRowMap = Map<string, EditingRowState>

export type LoanUsersTableEditCellProps = {
	row: LoanUserRow
	editingRows: EditingRowMap
	showRolesBasedOnUserRole?: boolean
	onRoleChange: (e: SelectChangeEvent<unknown>, rowId: string) => void
	onRoleSave: (row: LoanUserRow) => void
	onCancel: (row: LoanUserRow) => void
}

const LoanUsersTableRoleAssignCell = ({
	row,
	editingRows,
	showRolesBasedOnUserRole,
	onRoleChange,
	onRoleSave,
	onCancel,
}: LoanUsersTableEditCellProps) => {
	const { formatMessage } = useIntl()

	const editingRow = editingRows.get(row.id)
	const isEditing = !!editingRow
	const currentEditingRole = editingRow?.role ?? row.role
	const loading = !!editingRow?.loading

	const allowedRoles =
		showRolesBasedOnUserRole && row.userRole
			? getAllowedRolesForUser(row.userRole)
			: undefined

	const roleOptionList = useRoleList({
		includeRoles: allowedRoles,
	})

	const handleRoleChange = (e: SelectChangeEvent<unknown>) =>
		onRoleChange(e, row.id)

	const handleRoleSave = () => onRoleSave(row)

	const handleCancel = () => onCancel(row)

	if (isEditing) {
		return (
			<div className="flex gap-2 items-start pt-[20px]">
				<FormControl size="small" sx={{ width: 200 }}>
					<Select<LabelValue>
						menuItems={roleOptionList}
						selectProps={{
							value: currentEditingRole,
							onChange: handleRoleChange,
						}}
						disabled={loading}
					/>
				</FormControl>
				{loading ? (
					<CircularProgress size={16} sx={{ mt: '10px' }} />
				) : (
					<>
						<IconButton
							size="small"
							onClick={handleRoleSave}
							tooltipTitle={formatMessage({ id: 'global.save' })}
						>
							<CheckIcon color="success" />
						</IconButton>
						<IconButton
							size="small"
							onClick={handleCancel}
							tooltipTitle={formatMessage({ id: 'global.cancel' })}
						>
							<CancelIcon color="error" />
						</IconButton>
					</>
				)}
			</div>
		)
	}

	return addSpacesToPascalCase(row.role)
}

export default LoanUsersTableRoleAssignCell
